import type { IdentityV1 } from '@alpod-ai-writer/backend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Label, TextInput } from 'flowbite-react';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import PhoneInput, { isPossiblePhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { useLocation, useSearchParams } from 'react-router';

import { useApi } from '../../../api/useApi.hook';
import PasswordInput from '../../../components/passwordInput/passwordInput.component';
import { isPasswordValid } from '../../../components/passwordInput/passwordRules';
import PasswordStrengthIndicator from '../../../components/passwordInput/passwordStrengthIndicator.component';
import { BillingForm } from '../../components/account/billingForm.component';
import { AppHeader } from '../../components/appHeader/appHeader.component';
import { ToastNotificationType } from '../../components/toastNotification/toastNotifications.context';
import { useToastNotifications } from '../../components/toastNotification/useToastNotifications.hook';

interface PersonalForm {
    name: string;
    phone: string;
}

interface PasswordForm {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
}

const AccountSettingsView = () => {
    const api = useApi();
    const queryClient = useQueryClient();
    const toast = useToastNotifications();
    const [editBilling, setEditBilling] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const backLink = (location.state as { from?: string } | null)?.from ?? '/';

    const { data: identity } = useQuery({
        queryKey: ['my-identity'],
        queryFn: () => api.v1.identities.getMyIdentity(),
    });

    const { data: authMethods, refetch: refetchAuthMethods } = useQuery({
        queryKey: ['my-auth-methods'],
        queryFn: () => api.v1.identities.getMyAuthMethods(),
    });
    const isGoogleConnected = authMethods?.items.some(m => m.provider === 'google') ?? false;
    const googleAccountKey = authMethods?.items.find(m => m.provider === 'google')?.accountKey;

    useEffect(() => {
        if (searchParams.get('googleConnected') === 'true') {
            toast.showToast({ type: ToastNotificationType.SUCCESS, message: 'Google účet byl úspěšně propojen.' });
            setSearchParams({}, { replace: true });
            refetchAuthMethods();
        }
    }, []);

    const connectGoogle = useMutation({
        mutationFn: () => api.v1.auth.getGoogleConnectUrl(),
        onSuccess: ({ authUrl }) => { window.location.href = authUrl; },
        onError: () => toast.showToast({ type: ToastNotificationType.ERROR, message: 'Nepodařilo se získat odkaz pro propojení.' }),
    });

    const disconnectGoogle = useMutation({
        mutationFn: () => api.v1.identities.disconnectAuthMethod('google', googleAccountKey!),
        onSuccess: () => {
            toast.showToast({ type: ToastNotificationType.SUCCESS, message: 'Google účet byl odpojen.' });
            refetchAuthMethods();
        },
        onError: () => toast.showToast({ type: ToastNotificationType.ERROR, message: 'Odpojení se nezdařilo.' }),
    });

    const personalForm = useForm<PersonalForm>({
        values: {
            name: (identity?.userFields['name'] as string) ?? '',
            phone: ((identity?.userFields['phone'] as string) ?? '').replace(/\s/g, ''),
        },
    });
    const phone = personalForm.watch('phone');

    const passwordForm = useForm<PasswordForm>({
        defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
    });

    const savePersonal = useMutation({
        mutationFn: (update: IdentityV1.OwnUpdate) => api.v1.identities.updateMyIdentity(update),
        onSuccess: () => {
            toast.showToast({ type: ToastNotificationType.SUCCESS, message: 'Osobní údaje byly uloženy.' });
            queryClient.invalidateQueries({ queryKey: ['my-identity'] });
        },
        onError: () => {
            toast.showToast({ type: ToastNotificationType.ERROR, message: 'Uložení se nepodařilo. Zkuste to znovu.' });
        },
    });

    const changePassword = useMutation({
        mutationFn: ({ currentPassword, newPassword }: PasswordForm) =>
            api.v1.auth.provider.password.changePassword(currentPassword, newPassword),
        onSuccess: () => {
            toast.showToast({ type: ToastNotificationType.SUCCESS, message: 'Heslo bylo úspěšně změněno.' });
            passwordForm.reset();
        },
        onError: () => {
            toast.showToast({ type: ToastNotificationType.ERROR, message: 'Nesprávné aktuální heslo.' });
        },
    });

    const onSavePersonal = useCallback((data: PersonalForm) => {
        if (!identity) return;
        savePersonal.mutate({
            ...identity,
            name: data.name,
            userFields: { ...identity.userFields, name: data.name, phone: data.phone },
        });
    }, [identity, savePersonal]);

    const onChangePassword = useCallback((data: PasswordForm) => {
        changePassword.mutate(data);
    }, [changePassword]);

    return (
        <>
            <AppHeader pageTitle='Nastavení účtu' backLink={backLink} />

            <div className='p-4 pt-16 md:pt-20 md:pb-12'>
                <div className='max-w-xl mx-auto flex flex-col gap-6'>

                    {/* Personal info */}
                    <section className='border rounded-lg p-5 bg-white'>
                        <h2 className='text-lg font-semibold text-gray-900 mb-4'>Osobní údaje</h2>
                        <form onSubmit={personalForm.handleSubmit(onSavePersonal)} className='flex flex-col gap-4'>
                            <div>
                                <Label htmlFor='name' className='mb-1 block'>Jméno a příjmení</Label>
                                <TextInput
                                    id='name'
                                    type='text'
                                    placeholder='Jan Novák'
                                    {...personalForm.register('name', { required: 'Jméno je povinné' })}
                                    color={personalForm.formState.errors.name ? 'failure' : 'default'}
                                />
                                {personalForm.formState.errors.name && (
                                    <p className='text-sm text-red-500 mt-1'>{personalForm.formState.errors.name.message}</p>
                                )}
                            </div>
                            <div>
                                <Label className='mb-1 block'>E-mail</Label>
                                <TextInput value={identity?.email ?? ''} disabled />
                            </div>
                            <div>
                                <Label htmlFor='phone' className='mb-1 block'>Telefonní číslo</Label>
                                <PhoneInput
                                    id='phone'
                                    {...personalForm.register('phone', {
                                        validate: (v) => !v || isPossiblePhoneNumber(v) || 'Zadejte platné telefonní číslo.',
                                    })}
                                    value={phone}
                                    placeholder='Zadejte své telefonní číslo'
                                    onChange={(v) => personalForm.setValue('phone', v?.toString() || '')}
                                    defaultCountry='CZ'
                                    international
                                    withCountryCallingCode
                                    addInternationalOption={false}
                                    countries={['CZ', 'SK']}
                                    inputComponent={TextInput}
                                />
                                {personalForm.formState.errors.phone && (
                                    <p className='text-sm text-red-500 mt-1'>{personalForm.formState.errors.phone.message}</p>
                                )}
                            </div>
                            <Button type='submit' color='primary' disabled={savePersonal.isPending} className='self-start'>
                                {savePersonal.isPending ? 'Ukládám...' : 'Uložit'}
                            </Button>
                        </form>
                    </section>

                    {/* Billing info */}
                    <section className='border rounded-lg p-5 bg-white'>
                        <div className='flex items-center justify-between mb-4'>
                            <h2 className='text-lg font-semibold text-gray-900'>Fakturační údaje</h2>
                            {!editBilling && identity?.userFields['billingResidency'] && (
                                <button type='button' className='hyperlink text-sm' onClick={() => setEditBilling(true)}>
                                    Upravit
                                </button>
                            )}
                        </div>

                        {!editBilling && identity?.userFields['billingResidency'] ? (
                            <div className='text-sm text-gray-700 flex flex-col gap-1'>
                                <p><span className='font-medium w-36 inline-block'>Jméno:</span>{identity.userFields['billingName'] as string}</p>
                                <p><span className='font-medium w-36 inline-block'>Ulice:</span>{identity.userFields['billingStreet'] as string} {identity.userFields['billingStreetNumber'] as string ?? ''}</p>
                                <p><span className='font-medium w-36 inline-block'>Město:</span>{identity.userFields['billingCity'] as string}</p>
                                <p><span className='font-medium w-36 inline-block'>PSČ:</span>{identity.userFields['billingPostcode'] as string}</p>
                                <p><span className='font-medium w-36 inline-block'>Země:</span>{identity.userFields['billingResidency'] === 'CZ' ? 'Česká republika' : 'Slovenská republika'}</p>
                            </div>
                        ) : (
                            <BillingForm
                                submitLabel='Uložit fakturační údaje'
                                showSuccessToast
                                onSave={() => setEditBilling(false)}
                            />
                        )}
                    </section>

                    {/* Google SSO */}
                    <section className='border rounded-lg p-5 bg-white'>
                        <h2 className='text-lg font-semibold text-gray-900 mb-4'>Přihlášení přes Google</h2>
                        {isGoogleConnected ? (
                            <div className='flex items-center justify-between'>
                                <div className='flex items-center gap-2 text-sm text-gray-700'>
                                    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" className='shrink-0'>
                                        <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                                        <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                                        <path d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" fill="#FBBC05"/>
                                        <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z" fill="#EA4335"/>
                                    </svg>
                                    <span>Google účet je propojen</span>
                                </div>
                                <Button
                                    color='failure'
                                    size='sm'
                                    onClick={() => disconnectGoogle.mutate()}
                                    disabled={disconnectGoogle.isPending}
                                >
                                    {disconnectGoogle.isPending ? 'Odpojuji...' : 'Odpojit'}
                                </Button>
                            </div>
                        ) : (
                            <div className='flex items-center justify-between'>
                                <p className='text-sm text-gray-500'>Google účet není propojen</p>
                                <Button
                                    color='light'
                                    size='sm'
                                    onClick={() => connectGoogle.mutate()}
                                    disabled={connectGoogle.isPending}
                                >
                                    {connectGoogle.isPending ? 'Přesměrovávám...' : 'Propojit Google'}
                                </Button>
                            </div>
                        )}
                    </section>

                    {/* Change password */}
                    <section className='border rounded-lg p-5 bg-white'>
                        <h2 className='text-lg font-semibold text-gray-900 mb-4'>Změna hesla</h2>
                        <form onSubmit={passwordForm.handleSubmit(onChangePassword)} className='flex flex-col gap-4'>
                            <div>
                                <Label htmlFor='currentPassword' className='mb-1 block'>Aktuální heslo</Label>
                                <PasswordInput
                                    id='currentPassword'
                                    placeholder='Aktuální heslo'
                                    {...passwordForm.register('currentPassword', { required: 'Zadejte aktuální heslo' })}
                                    color={passwordForm.formState.errors.currentPassword ? 'failure' : ''}
                                />
                                {passwordForm.formState.errors.currentPassword && (
                                    <p className='text-sm text-red-500 mt-1'>{passwordForm.formState.errors.currentPassword.message}</p>
                                )}
                            </div>
                            <div>
                                <Label htmlFor='newPassword' className='mb-1 block'>Nové heslo</Label>
                                <PasswordInput
                                    id='newPassword'
                                    placeholder='Nové heslo'
                                    {...passwordForm.register('newPassword', {
                                        required: 'Zadejte nové heslo',
                                        validate: v => isPasswordValid(v) || 'Heslo nesplňuje požadavky',
                                    })}
                                    color={passwordForm.formState.errors.newPassword ? 'failure' : ''}
                                />
                                {passwordForm.formState.errors.newPassword && (
                                    <p className='text-sm text-red-500 mt-1'>{passwordForm.formState.errors.newPassword.message}</p>
                                )}
                            </div>
                            <div>
                                <Label htmlFor='confirmPassword' className='mb-1 block'>Potvrďte nové heslo</Label>
                                <PasswordInput
                                    id='confirmPassword'
                                    placeholder='Zopakujte nové heslo'
                                    {...passwordForm.register('confirmPassword', {
                                        required: 'Potvrďte nové heslo',
                                        validate: (v) => v === passwordForm.watch('newPassword') || 'Hesla se neshodují',
                                    })}
                                    color={passwordForm.formState.errors.confirmPassword ? 'failure' : ''}
                                />
                                {passwordForm.formState.errors.confirmPassword && (
                                    <p className='text-sm text-red-500 mt-1'>{passwordForm.formState.errors.confirmPassword.message}</p>
                                )}
                            </div>
                            <PasswordStrengthIndicator password={passwordForm.watch('newPassword')} />
                            <Button type='submit' color='primary' disabled={changePassword.isPending || !isPasswordValid(passwordForm.watch('newPassword'))} className='self-start'>
                                {changePassword.isPending ? 'Ukládám...' : 'Změnit heslo'}
                            </Button>
                        </form>
                    </section>

                </div>
            </div>
        </>
    );
};

export default AccountSettingsView;
